import React, { useEffect, useState } from "react";
import { adminApi, getAdminToken, API_BASE_URL } from "../api";

interface AnalyticsSummary {
  totalSessions: number;
  totalPageViews?: number;
  totalSearches: number;
  totalCartAdds: number;
  topProducts: Array<{ title: string; adds: number; totalQuantity: number; revenue: number }>;
  topRegions: Array<{ state: string; city: string; sessionsCount: number; cartAdds: number }>;
  topSearches: Array<{ query: string; count: number }>;
  topPages?: Array<{ path: string; title: string; viewsCount: number }>;
}

export function AnalyticsPage() {
  const [data, setData] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const summary = await adminApi.analyticsSummary();
      setData(summary);
    } catch (err: any) {
      setError(err.message || "Erro de rede.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const handleExportCSV = async () => {
    try {
      const token = getAdminToken();
      const res = await fetch(`${API_BASE_URL}/api/admin/analytics/export/csv`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `melhot_analytics_remarketing_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e: any) {
      alert("Erro ao exportar CSV: " + e.message);
    }
  };

  return (
    <div style={{ padding: "28px", maxWidth: "1400px", margin: "0 auto", fontFamily: "Mulish, system-ui, sans-serif" }}>
      {/* HEADER BAR */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "28px", flexWrap: "wrap", gap: "16px" }}>
        <div>
          <h1 style={{ fontSize: "26px", fontWeight: 800, color: "#0f172a", margin: 0 }}>
            Inteligência de Tráfego & Remarketing
          </h1>
          <p style={{ fontSize: "14px", color: "#64748b", margin: "4px 0 0 0" }}>
            Captura na borda via CF Pages/Workers + Supabase de páginas visitadas, localização, buscas e desejos no carrinho
          </p>
        </div>

        <div style={{ display: "flex", gap: "12px" }}>
          <button
            onClick={fetchAnalytics}
            style={{
              padding: "10px 18px",
              background: "#ffffff",
              border: "1px solid #cbd5e1",
              borderRadius: "8px",
              fontWeight: 700,
              fontSize: "14px",
              color: "#334155",
              cursor: "pointer"
            }}
          >
            🔄 Atualizar
          </button>

          <button
            onClick={handleExportCSV}
            style={{
              padding: "10px 20px",
              background: "#059669",
              color: "#ffffff",
              border: "none",
              borderRadius: "8px",
              fontWeight: 800,
              fontSize: "14px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              boxShadow: "0 2px 8px rgba(5, 150, 105, 0.25)"
            }}
          >
            📊 Exportar CSV / Power BI
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: "60px", textAlign: "center", color: "#64748b", fontSize: "16px" }}>
          Carregando inteligência de remarketing...
        </div>
      ) : error ? (
        <div style={{ padding: "20px", background: "#fef2f2", color: "#dc2626", borderRadius: "8px" }}>
          {error}
        </div>
      ) : data ? (
        <>
          {/* KPI CARDS */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "20px", marginBottom: "32px" }}>
            <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
              <span style={{ fontSize: "13px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Sessões Rastreadas</span>
              <div style={{ fontSize: "32px", fontWeight: 900, color: "#0f172a", marginTop: "8px" }}>
                {data.totalSessions}
              </div>
            </div>

            <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
              <span style={{ fontSize: "13px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Páginas Visitadas</span>
              <div style={{ fontSize: "32px", fontWeight: 900, color: "#7c3aed", marginTop: "8px" }}>
                {data.totalPageViews || 0}
              </div>
            </div>

            <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
              <span style={{ fontSize: "13px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Adições ao Carrinho</span>
              <div style={{ fontSize: "32px", fontWeight: 900, color: "#2563eb", marginTop: "8px" }}>
                {data.totalCartAdds}
              </div>
            </div>

            <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
              <span style={{ fontSize: "13px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Buscas na Loja</span>
              <div style={{ fontSize: "32px", fontWeight: 900, color: "#d97706", marginTop: "8px" }}>
                {data.totalSearches}
              </div>
            </div>

            <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
              <span style={{ fontSize: "13px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>Taxa de Intenção de Compra</span>
              <div style={{ fontSize: "32px", fontWeight: 900, color: "#059669", marginTop: "8px" }}>
                {data.totalSessions ? ((data.totalCartAdds / data.totalSessions) * 100).toFixed(1) + "%" : "0%"}
              </div>
            </div>
          </div>

          {/* TABLES GRID */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginBottom: "32px" }}>
            {/* TOP PRODUCTS */}
            <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
              <h2 style={{ fontSize: "18px", fontWeight: 800, color: "#0f172a", marginBottom: "16px" }}>
                🛒 Ranking de Produtos Mais Adicionados ao Carrinho
              </h2>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "14px" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #cbd5e1", color: "#475569" }}>
                      <th style={{ padding: "10px 8px" }}>Produto</th>
                      <th style={{ padding: "10px 8px", textAlign: "right" }}>Vezes Adicionado</th>
                      <th style={{ padding: "10px 8px", textAlign: "right" }}>Qtd Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topProducts.length === 0 ? (
                      <tr><td colSpan={3} style={{ padding: "16px", color: "#94a3b8", textAlign: "center" }}>Nenhuma adição registrada ainda.</td></tr>
                    ) : (
                      data.topProducts.slice(0, 10).map((prod, idx) => (
                        <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ padding: "12px 8px", fontWeight: 600, color: "#0f172a" }}>{prod.title}</td>
                          <td style={{ padding: "12px 8px", textAlign: "right", fontWeight: 700, color: "#2563eb" }}>{prod.adds}</td>
                          <td style={{ padding: "12px 8px", textAlign: "right", color: "#64748b" }}>{prod.totalQuantity}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* TOP VISITED PAGES */}
            <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
              <h2 style={{ fontSize: "18px", fontWeight: 800, color: "#0f172a", marginBottom: "16px" }}>
                📄 Ranking de Páginas Mais Visitadas na Loja
              </h2>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "14px" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #cbd5e1", color: "#475569" }}>
                      <th style={{ padding: "10px 8px" }}>Caminho / URL</th>
                      <th style={{ padding: "10px 8px", textAlign: "right" }}>Visualizações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(!data.topPages || data.topPages.length === 0) ? (
                      <tr><td colSpan={2} style={{ padding: "16px", color: "#94a3b8", textAlign: "center" }}>Nenhuma página visitada ainda.</td></tr>
                    ) : (
                      data.topPages.slice(0, 10).map((pg, idx) => (
                        <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ padding: "12px 8px", fontWeight: 600, color: "#0f172a" }}>
                            <span style={{ fontSize: "13px", color: "#334155", display: "block" }}>{pg.path}</span>
                          </td>
                          <td style={{ padding: "12px 8px", textAlign: "right", fontWeight: 700, color: "#7c3aed" }}>{pg.viewsCount}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginBottom: "32px" }}>
            {/* TOP REGIONS FOR REMARKETING */}
            <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
              <h2 style={{ fontSize: "18px", fontWeight: 800, color: "#0f172a", marginBottom: "16px" }}>
                📍 Demanda Geográfica por Região (UF / Cidade)
              </h2>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "14px" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #cbd5e1", color: "#475569" }}>
                      <th style={{ padding: "10px 8px" }}>Estado (UF) / Cidade</th>
                      <th style={{ padding: "10px 8px", textAlign: "right" }}>Sessões</th>
                      <th style={{ padding: "10px 8px", textAlign: "right" }}>Carrinhos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topRegions.length === 0 ? (
                      <tr><td colSpan={3} style={{ padding: "16px", color: "#94a3b8", textAlign: "center" }}>Nenhuma localização capturada ainda.</td></tr>
                    ) : (
                      data.topRegions.slice(0, 10).map((reg, idx) => (
                        <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ padding: "12px 8px", fontWeight: 600, color: "#0f172a" }}>
                            <span style={{ display: "inline-block", background: "#e2e8f0", padding: "2px 8px", borderRadius: "4px", fontSize: "12px", marginRight: "8px", fontWeight: 800 }}>{reg.state || "BR"}</span>
                            {reg.city || "Região Geral"}
                          </td>
                          <td style={{ padding: "12px 8px", textAlign: "right", color: "#64748b" }}>{reg.sessionsCount}</td>
                          <td style={{ padding: "12px 8px", textAlign: "right", fontWeight: 700, color: "#059669" }}>{reg.cartAdds}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* TOP SEARCHES */}
            <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 4px rgba(0,0,0,0.02)" }}>
              <h2 style={{ fontSize: "18px", fontWeight: 800, color: "#0f172a", marginBottom: "16px" }}>
                🔍 Termos Mais Buscados na Barra de Pesquisa
              </h2>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "14px" }}>
                  <thead>
                    <tr style={{ borderBottom: "2px solid #cbd5e1", color: "#475569" }}>
                      <th style={{ padding: "10px 8px" }}>Termo de Busca</th>
                      <th style={{ padding: "10px 8px", textAlign: "right" }}>Pesquisas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topSearches.length === 0 ? (
                      <tr><td colSpan={2} style={{ padding: "16px", color: "#94a3b8", textAlign: "center" }}>Nenhuma busca pesquisada na loja ainda.</td></tr>
                    ) : (
                      data.topSearches.slice(0, 10).map((st, idx) => (
                        <tr key={idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ padding: "12px 8px", fontWeight: 600, color: "#0f172a" }}>"{st.query}"</td>
                          <td style={{ padding: "12px 8px", textAlign: "right", fontWeight: 700, color: "#d97706" }}>{st.count}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
